import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";

const DEFAULT_TAG_BACKGROUND = "#9e9e9e";
const LIGHT_BACKGROUND_LUMINANCE = 0.55;

const sizeStyles = {
  small: {
    fontSize: "0.6875rem",
    lineHeight: 1,
    px: 1,
    py: 0.25,
    minHeight: 20,
    borderRadius: "10px",
  },
  big: {
    fontSize: "0.8125rem",
    lineHeight: 1,
    px: 1.5,
    py: 0.5,
    minHeight: 26,
    borderRadius: "12px",
  },
};

function parseRgb(color) {
  if (!color || typeof color !== "string") {
    return null;
  }

  const normalized = color.trim().toLowerCase();
  if (normalized === "white" || normalized === "#fff" || normalized === "#ffffff") {
    return { r: 255, g: 255, b: 255 };
  }
  if (normalized === "grey" || normalized === "gray") {
    return { r: 128, g: 128, b: 128 };
  }

  let hex = normalized.startsWith("#") ? normalized.slice(1) : null;
  if (!hex) {
    return null;
  }
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map(char => char + char)
      .join("");
  }
  if (hex.length !== 6) {
    return null;
  }

  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some(channel => Number.isNaN(channel))) {
    return null;
  }

  return { r, g, b };
}

function getLuminance({ r, g, b }) {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function resolveTagColors(color, theme) {
  const backgroundColor = color && String(color).trim() ? color : DEFAULT_TAG_BACKGROUND;
  const rgb = parseRgb(backgroundColor);
  const textColor =
    rgb && getLuminance(rgb) > LIGHT_BACKGROUND_LUMINANCE
      ? theme.palette.text.primary
      : theme.palette.common.white;

  return { backgroundColor, color: textColor };
}

const TagComponent = ({ text, color, size }) => {
  const resolvedSize = size === "big" ? "big" : "small";
  return (
    <Box
      component="span"
      sx={theme => {
        const tagColors = resolveTagColors(color, theme);
        return {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: tagColors.backgroundColor,
          color: tagColors.color,
          fontWeight: 500,
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          ...sizeStyles[resolvedSize],
        };
      }}
    >
      {text}
    </Box>
  );
};

TagComponent.defaultProps = {
  size: "small",
  color: "grey",
};

TagComponent.propTypes = {
  text: PropTypes.string.isRequired,
  color: PropTypes.string,
  size: PropTypes.string,
};

export default TagComponent;
